import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import { useAtomValue } from "jotai";
import { Fragment, useCallback, useState } from "react";
import type { FunctionComponent } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { config } from "shared/data";
import { accountAtom } from "shared/lib/account";

import { usePostiesFormDrawerContext } from "scmp-app/components/posties/form-drawer/contexts";
import type {
  MergedStudentFormFields,
  StudentFormStaticDataType,
} from "scmp-app/components/posties/form-drawer/types";

import { getAnswer, getSchool, renderQuestion, renderStaticField } from "./helpers";
import { ButtonContainer, FormContainer, StyledButton } from "./styles";
import type {
  Education,
  School,
  StudentFormFields,
  StudentStaticFieldValue,
  StudentStaticResult,
} from "./types";

type Props = {
  className?: string;
  fields: StudentFormFields;
};
export const PostiesStudentForm: FunctionComponent<Props> = ({
  className,
  fields: { defaultValues, questionsIds, schema, staticFields },
}) => {
  const { user } = useAtomValue(accountAtom);
  const { formData, formDrawer, setFormDrawer } = usePostiesFormDrawerContext();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const formProps = useForm({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(yup.object().shape(schema)),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit: SubmitHandler<StudentStaticResult> = useCallback(
    async inputData => {
      setIsSubmitting(true);
      if (!isSubmitting) {
        if (!executeRecaptcha) return;
        const recaptchaToken = await executeRecaptcha("form_submit");

        const omitKeys = new Set([
          inputData.dateOfBirth ? "" : "dateOfBirth",
          "otherYearOrGrade",
          "school",
          "yearOrGrade",
          ...questionsIds,
        ]);
        const dataOmit = Object.keys(inputData)
          .filter(key => !omitKeys.has(key))
          .reduce(
            (accumulator, key) => {
              accumulator[key] = inputData[key];
              return accumulator;
            },
            {} as Record<string, unknown>,
          );

        const modifiedData = {
          answers: getAnswer(inputData, questionsIds),
          education:
            typeof inputData.yearOrGrade === "object"
              ? (inputData.yearOrGrade as Education)?.documentId
              : "",
          formId: formDrawer.documentId,
          otherEducation: inputData.otherYearOrGrade,
          recaptchaToken,
          school: getSchool(inputData.school as null | School),
          userUuid: user?.uuid ?? "",
          ...dataOmit,
        };

        try {
          const response = await axios.post<{ data: { createSubmission: { success: boolean } } }>(
            config.graphql.studentService.host,
            {
              query: `
              mutation CreateSubmission($input: SubmissionInput!) {
                createSubmission(input: $input) {
                  documentId
                  success
                  message
                }
              }
            `,
              variables: {
                input: modifiedData,
              },
            },
            {
              headers: {
                apiKey: config.graphql.studentService.apiKey,
                "Content-Type": "application/json",
              },
            },
          );

          if (response.data?.data?.createSubmission?.success) {
            setFormDrawer?.(previousState => ({ ...previousState, messageBox: "thank-you" }));
          } else {
            setFormDrawer?.(previousState => ({ ...previousState, messageBox: "invalid" }));
          }

          setIsSubmitting(false);
        } catch (error) {
          setIsSubmitting(false);
          console.error("Submission error:", error);
        }
      }
    },
    [
      executeRecaptcha,
      questionsIds,
      formDrawer.documentId,
      user?.uuid,
      setFormDrawer,
      isSubmitting,
    ],
  );

  const renderFields = () =>
    Object.entries(staticFields).map(([key, value]) => {
      const field = value as StudentFormStaticDataType & StudentStaticFieldValue;
      if (field.display) {
        return (
          <Fragment key={key}>
            {renderStaticField(field, key as keyof MergedStudentFormFields, formProps)}
          </Fragment>
        );
      }
      return null;
    });

  return (
    <FormContainer className={className} noValidate onSubmit={formProps.handleSubmit(onSubmit)}>
      {renderFields()}
      {formData?.questions?.map(question => renderQuestion(question, formProps))}
      <ButtonContainer>
        <StyledButton variant="button">Submit</StyledButton>
      </ButtonContainer>
    </FormContainer>
  );
};

PostiesStudentForm.displayName = "PostiesStudentForm";
